import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sparkles, ImagePlus, Trash2, Lock, Upload } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import { toast } from "sonner";
import { Link } from "react-router-dom";

/**
 * AI Logo Studio — inline card on the vendor profile edit page.
 * Pro-gated (backend enforces via `_require_pro_vendor`). Trial vendors have
 * access too because trials satisfy the same guard on the server.
 *
 * Flow:
 *   1. Vendor optionally types an extra direction (e.g. "make it more moody").
 *   2. Clicks Generate → POST /api/vendor/logo/generate → returns { logo_url }.
 *   3. Preview appears with "Use this logo" (writes to parent state via
 *      onSelect) and "Try again". Regenerating does NOT overwrite the saved
 *      logo — parent only persists when the whole profile form is saved.
 *   4. Also supports removing the current logo (sets parent value to "").
 *
 * Daily limit: server caps at 3 generations per vendor per UTC day. We
 * fetch the current quota on mount so we can show 'N of 3 used today'.
 */
export default function LogoStudioCard({ currentLogoUrl, onSelect, businessName }) {
  const { user } = useAuth();
  const [direction, setDirection] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // { logo_url, prompt }
  const [errorMsg, setErrorMsg] = useState("");
  const [quota, setQuota] = useState(null); // { used, limit, remaining }

  // Backend enforces this — but we mirror on the client so free vendors don't
  // even see the direction textarea. Trial vendors count as Pro.
  const isPro = (user?.plan === "pro") || Boolean(user?.on_trial);
  const upgradeAvailable = user?.trial_available;

  // Fetch today's usage once when the card first mounts for a Pro/trial
  // vendor. Free vendors never hit this — the pro gate handles them.
  useEffect(() => {
    if (!isPro) return;
    let cancelled = false;
    api.get("/vendor/logo/quota")
      .then(({ data }) => { if (!cancelled) setQuota(data); })
      .catch(() => { /* non-critical — just skip the quota chip */ });
    return () => { cancelled = true; };
  }, [isPro]);

  const generate = async () => {
    setBusy(true);
    setErrorMsg("");
    try {
      const { data } = await api.post("/vendor/logo/generate", {
        extra_direction: direction || null,
      });
      setPreview({ logo_url: data.logo_url, prompt: data.prompt });
      if (typeof data.used_today === "number") {
        setQuota({ used: data.used_today, limit: data.daily_limit,
                   remaining: data.remaining_today });
      }
    } catch (err) {
      // 429 → daily cap hit. Backend sends structured detail so we can
      // update the quota chip AND show a friendlier message.
      if (err.response?.status === 429) {
        const d = err.response.data?.detail || {};
        if (d.used && d.limit) {
          setQuota({ used: d.used, limit: d.limit, remaining: 0 });
        }
      }
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      const msg = detail || "Couldn't generate a logo — please try again.";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const usePreview = () => {
    if (!preview) return;
    onSelect(preview.logo_url);
    toast.success("Logo picked. Save your profile to apply it.");
    setPreview(null);
    setDirection("");
  };

  const removeLogo = () => {
    onSelect("");
    setPreview(null);
    toast.success("Logo cleared. Save your profile to apply it.");
  };

  return (
    <div className="card-warm rounded-sm p-5" data-testid="logo-studio-card">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="h-4 w-4" style={{ color: "var(--moss)" }} />
        <div className="overline" style={{ color: "var(--moss)" }}>Logo Studio</div>
      </div>
      <p className="text-sm mb-4" style={{ color: "var(--ink-soft)" }}>
        Upload the logo you already use, or spin up a fresh one with the AI generator (Maker Pro).
        Nothing is applied until you save your profile.
      </p>

      {/* Current logo (persisted) */}
      <div className="grid sm:grid-cols-[auto,1fr] gap-4 items-start mb-4">
        <div className="w-28 h-28 rounded-sm border-2 flex items-center justify-center overflow-hidden"
                style={{ borderColor: "var(--sand)", backgroundColor: "var(--paper-warm)" }}
                data-testid="logo-current">
          {currentLogoUrl ? (
            <img src={currentLogoUrl} alt="Current logo" className="w-full h-full object-cover" />
          ) : (
            <ImagePlus className="h-8 w-8" style={{ color: "var(--ink-soft)" }} />
          )}
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest mb-1"
                style={{ color: "var(--ink-soft)" }}>
            Current logo
          </div>
          <p className="text-sm mb-2" style={{ color: "var(--ink-soft)" }}>
            {currentLogoUrl
              ? "Shows on your public vendor page and event applications."
              : "No logo yet. Upload one below, or generate one with the AI Studio."}
          </p>
          {currentLogoUrl && (
            <Button type="button" size="sm" data-testid="logo-remove-btn"
                    onClick={removeLogo}
                    className="btn-outline rounded-full">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
            </Button>
          )}
        </div>
      </div>

      {/* Upload your own logo — available to ALL vendors (not Pro-gated).
          This is just uploading an existing brand mark, not AI generation. */}
      <div
        className="rounded-sm p-4 mb-4 flex items-start gap-3 flex-wrap"
        style={{ backgroundColor: "var(--paper-warm)", border: "1px dashed var(--sand)" }}
        data-testid="logo-upload-panel"
      >
        <Upload className="h-4 w-4 mt-1" style={{ color: "var(--avt-teal)" }} />
        <div className="flex-1 min-w-[220px]">
          <div className="text-xs font-semibold uppercase tracking-widest"
               style={{ color: "var(--avt-teal)" }}>
            Upload your own
          </div>
          <p className="text-sm mt-1" style={{ color: "var(--ink-soft)" }}>
            Already have a logo? Drop a PNG, JPG, or SVG (recommended square, up to 5&nbsp;MB).
            It goes live the moment you save your profile.
          </p>
        </div>
        <ImageUploader
          label="Upload logo"
          bucket="logos"
          data-testid="logo-upload-btn"
          onUploaded={(url) => {
            onSelect(url);
            toast.success("Logo uploaded. Save your profile to apply it.");
          }}
        />
      </div>

      {/* Pro gate for free vendors (AI generator only) */}
      {!isPro ? (
        <div className="rounded-sm p-4 flex items-start gap-3"
                style={{ backgroundColor: "var(--paper-warm)", border: "1px dashed var(--sand)" }}
                data-testid="logo-pro-gate">
          <Lock className="h-4 w-4 mt-0.5" style={{ color: "var(--moss)" }} />
          <div className="flex-1 text-sm">
            <div className="font-semibold" style={{ color: "var(--ink)" }}>AI generator on Maker Pro</div>
            <p style={{ color: "var(--ink-soft)" }}>
              Free vendors can upload their own logo above. The AI generator is included with
              Maker Pro along with the Business Assistant, Bookkeeping, and Mileage log.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {upgradeAvailable && (
                <Link to="/vendor/dashboard" data-testid="logo-start-trial-link">
                  <Button type="button" size="sm" className="btn-primary rounded-full">
                    Start 7-day free trial
                  </Button>
                </Link>
              )}
              <Link to="/pricing" data-testid="logo-see-pricing-link">
                <Button type="button" size="sm" className="btn-outline rounded-full">
                  See pricing
                </Button>
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-3">
            <Label className="text-xs uppercase tracking-widest">Extra direction (optional)</Label>
            <Textarea
              data-testid="logo-direction-input"
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
              rows={2}
              placeholder="Vibe or symbol you want — e.g. 'incorporate a crescent moon' or 'more moody'"
              className="mt-1"
              maxLength={400}
            />
            <div className="text-[11px] mt-1 text-right"
                    style={{ color: "var(--ink-soft)" }}>
              {direction.length}/400
            </div>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <Button
              type="button"
              data-testid="logo-generate-btn"
              onClick={generate}
              disabled={busy || !businessName?.trim() || quota?.remaining === 0}
              className="btn-primary rounded-full"
            >
              <Sparkles className="h-4 w-4 mr-1.5" />
              {busy ? "Generating…" : (currentLogoUrl ? "Regenerate" : "Generate logo")}
            </Button>
            {!businessName?.trim() && (
              <span className="text-xs self-center" style={{ color: "var(--terracotta)" }}
                        data-testid="logo-need-name-hint">
                Add your brand name up top first.
              </span>
            )}
            {quota && businessName?.trim() && (
              <span
                data-testid="logo-quota-chip"
                className="text-[11px] uppercase tracking-widest px-2 py-1 rounded-full"
                style={{
                  backgroundColor: quota.remaining === 0 ? "#fef2f2" : "var(--paper-warm)",
                  color: quota.remaining === 0 ? "#7f1d1d" : "var(--ink-soft)",
                  border: `1px solid ${quota.remaining === 0 ? "#fecaca" : "var(--sand)"}`,
                }}
              >
                {quota.remaining === 0
                  ? `Daily limit reached · resets tomorrow`
                  : `${quota.used}/${quota.limit} used today`}
              </span>
            )}
          </div>

          {errorMsg && (
            <div className="mt-3 text-sm rounded-sm p-3"
                    style={{ backgroundColor: "#fef2f2", color: "#7f1d1d", border: "1px solid #fecaca" }}
                    data-testid="logo-error">
              {errorMsg}
            </div>
          )}

          {preview && (
            <div className="mt-5 rounded-sm p-4" data-testid="logo-preview-card"
                    style={{ backgroundColor: "var(--paper-warm)", border: "1px solid var(--sand)" }}>
              <div className="grid sm:grid-cols-[auto,1fr] gap-4 items-start">
                <img src={preview.logo_url} alt="Generated logo preview"
                        className="w-32 h-32 rounded-sm object-cover"
                        data-testid="logo-preview-image" />
                <div>
                  <div className="overline mb-1" style={{ color: "var(--moss)" }}>
                    Fresh from Nano Banana
                  </div>
                  <p className="text-sm" style={{ color: "var(--ink-soft)" }}>
                    Love it? Pick this to swap into your profile — you&apos;ll still need to click{" "}
                    <span className="font-semibold" style={{ color: "var(--ink)" }}>Save profile</span> to apply.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button type="button" data-testid="logo-use-btn"
                                onClick={usePreview}
                                className="btn-primary rounded-full">
                      Use this logo
                    </Button>
                    <Button type="button" data-testid="logo-regenerate-btn"
                                onClick={generate}
                                disabled={busy || quota?.remaining === 0}
                                className="btn-outline rounded-full">
                      {busy ? "Generating…" : (quota?.remaining === 0 ? "Try again tomorrow" : "Try again")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
